import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiService } from '../services/apiService';
import { getHttpStatus } from '../api/authErrorHandling';
import { useAuthStore } from '../store/authStore';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { PositionStatusEnum } from '../../generated-src/client/positions/api';
import { getIndustryNameById, getStoredSpecializationName, getAreaNameById, fetchHHCountries, getCountryNameById } from '../services/hhDictionariesService';
import { formatSalaryDisplay } from '../utils/numberFormat';
import { EDUCATION_OPTIONS } from '../constants/vacancyFormConstants';
import { getHHVacancyById } from '../relevanter/services/api';
import { useVacancyStore } from '../store/vacancyStore';
import { ReactComponent as LinkCopyIcon } from '../assets/icons/link-copy.svg';
import { ReactComponent as LinkCopyCurrentIcon } from '../assets/icons/link-copy-current.svg';
import { RequirementsPanel } from '../components/vacancies/ai-assistant/RequirementsPanel';
import type { RequirementItem } from '../components/vacancies/ai-assistant/types';
import { VacancyStatusDropdown } from '../components/vacancies/components/vacancy-status-dropdown';
import aiPanelExpandIcon from '../assets/icons/ai-panel-expand.png';
import { getVacancyLoadAccessErrorMessage } from './vacancyLoadError';

const LinkCopyIconComponent = (LinkCopyIcon || (() => null)) as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const LinkCopyCurrentIconComponent = (LinkCopyCurrentIcon || (() => null)) as React.ComponentType<React.SVGProps<SVGSVGElement>>;

// Маппинг периода опыта для отображения
const getExperienceInPositionLabel = (value: string): string => {
  const labels: Record<string, string> = {
    noExperience: 'Без опыта',
    between1And3: 'От 1 до 3 лет',
    between3And6: 'От 3 до 6 лет',
    moreThan6: 'Более 6 лет',
  };
  return labels[value] || value;
};

const getExperiencePeriodLabel = (value: string): string => {
  const periodLabels: Record<string, string> = {
    'all_time': 'За всё время опыта',
    'last_year': 'За последний год',
    'last_three_years': 'За последние 3 года',
    'last_six_years': 'За последние 6 лет',
    // Старые значения для обратной совместимости
    'Нет опыта': 'Нет опыта',
    'От 1 года до 3 лет': 'От 1 года до 3 лет',
    'От 3 до 6 лет': 'От 3 до 6 лет',
    'Более 6 лет': 'Более 6 лет',
  };
  return periodLabels[value] || value;
};

type VacancyViewScopeUser = {
  id?: unknown;
  email?: unknown;
  companyId?: unknown;
} | null | undefined;

type LoadedVacancyIdentity = {
  id: number;
  scope: string | null;
};

const normalizeScopePart = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().toLowerCase();
  return normalized || null;
};

const getVacancyViewScope = (user: VacancyViewScopeUser): string | null => {
  const userPart = normalizeScopePart(user?.id) || normalizeScopePart(user?.email);
  if (!userPart) return null;

  const companyPart = normalizeScopePart(user?.companyId) || 'no-company';
  return `company:${companyPart}:user:${userPart}`;
};

export default function VacancyViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const colors = useBrandingColors();
  const setSelectedVacancy = useVacancyStore((state) => state.setSelectedVacancy);
  const vacancyViewUser = useAuthStore((state) => state.user);
  const vacancyViewScope = getVacancyViewScope(vacancyViewUser);
  const vacancyId = id ? parseInt(id, 10) : null;
  const currentVacancyIdRef = useRef<number | null>(vacancyId);
  const vacancyViewScopeRef = useRef<string | null>(vacancyViewScope);
  const vacancyLoadSeqRef = useRef(0);
  currentVacancyIdRef.current = vacancyId;
  vacancyViewScopeRef.current = vacancyViewScope;

  const [vacancy, setVacancy] = useState<any>(null);
  const [loadedVacancyIdentity, setLoadedVacancyIdentity] = useState<LoadedVacancyIdentity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [copiedLinkKey, setCopiedLinkKey] = useState<null | 'ai-screening' | 'hr-interview'>(null);
  const [creatorData, setCreatorData] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});
  const [industryName, setIndustryName] = useState<string>('');
  const [specializationName, setSpecializationName] = useState<string>('');
  const [cityNames, setCityNames] = useState<string[]>([]);
  const [citizenshipNames, setCitizenshipNames] = useState<string[]>([]);
  const [hhMainVacancy, setHhMainVacancy] = useState<{ id: string; name: string } | null>(null);
  const [hhAdditionalVacancies, setHhAdditionalVacancies] = useState<Array<{ id: string; name: string }>>([]);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [requirements, setRequirements] = useState<{
    required: RequirementItem[];
    optional: RequirementItem[];
  } | null>(null);
  const [showReqPanel, setShowReqPanel] = useState(true);

  // Sticky requirements panel: compute height imperatively so `h-full` inside resolves
  // to viewport-bounded space, matching the behaviour of the AI panel in edit mode.
  // Collapsed bookmark sidebar — fills the viewport (height set imperatively to track scroll position).
  const setupDynamicPanelHeight = useCallback((el: HTMLElement) => {
    const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
      while (node && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') return node;
        node = node.parentElement;
      }
      return null;
    };
    const scrollContainer = findScrollParent(el);
    const updatePanelHeight = () => {
      const elRect = el.getBoundingClientRect();
      const top = Math.max(elRect.top, 72);
      const height = window.innerHeight - top - 20;
      el.style.height = `${Math.max(height, 200)}px`;
    };
    updatePanelHeight();
    if (scrollContainer) scrollContainer.addEventListener('scroll', updatePanelHeight);
    window.addEventListener('resize', updatePanelHeight);
    return () => {
      if (scrollContainer) scrollContainer.removeEventListener('scroll', updatePanelHeight);
      window.removeEventListener('resize', updatePanelHeight);
    };
  }, []);
  const reqPanelCleanupRef = useRef<(() => void) | null>(null);
  const reqPanelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    if (reqPanelCleanupRef.current) {
      reqPanelCleanupRef.current();
      reqPanelCleanupRef.current = null;
    }
    if (el) reqPanelCleanupRef.current = setupDynamicPanelHeight(el) || null;
  }, [setupDynamicPanelHeight]);
  const reqBookmarkCleanupRef = useRef<(() => void) | null>(null);
  const reqBookmarkCallbackRef = useCallback((el: HTMLDivElement | null) => {
    if (reqBookmarkCleanupRef.current) {
      reqBookmarkCleanupRef.current();
      reqBookmarkCleanupRef.current = null;
    }
    if (el) reqBookmarkCleanupRef.current = setupDynamicPanelHeight(el) || null;
  }, [setupDynamicPanelHeight]);

  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textArea);
        return ok;
      } catch {
        return false;
      }
    }
  };

  const handleCopyLink = async (key: 'ai-screening' | 'hr-interview', url: string) => {
    const ok = await copyToClipboard(url);
    if (!ok) {
      toast.error('Не удалось скопировать');
      return;
    }
    setCopiedLinkKey(key);
    toast.success('Скопировано');
    setTimeout(() => setCopiedLinkKey(null), 2000);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!vacancy) {
      setRequirements(null);
      setIndustryName('');
      setSpecializationName('');
      setHhMainVacancy(null);
      setHhAdditionalVacancies([]);
      setCityNames([]);
      setCitizenshipNames([]);
      return;
    }

    let isCancelled = false;
    const requestVacancyId = typeof vacancy.id === 'number' ? vacancy.id : vacancyId;
    const requestScope = vacancyViewScope;
    const isCurrentDerivedRequest = () =>
      !isCancelled
      && currentVacancyIdRef.current === requestVacancyId
      && vacancyViewScopeRef.current === requestScope;
    const setIfCurrent = (setState: () => void) => {
      if (isCurrentDerivedRequest()) setState();
    };

    if (!isCurrentDerivedRequest()) {
      return () => {
        isCancelled = true;
      };
    }

    // Parse weighted requirements JSON stored on the position
    if ((vacancy as any).aiRequirementsWeighted) {
      try {
        const raw = (vacancy as any).aiRequirementsWeighted;
        const w = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setIfCurrent(() => setRequirements({
          required: (w.required || []).map((r: any, i: number) => ({ id: `req-${i}`, text: r.text, weight: r.weight ?? 7 })),
          optional: (w.optional || []).map((r: any, i: number) => ({ id: `opt-${i}`, text: r.text, weight: r.weight ?? 3 })),
        }));
      } catch (e) {
        console.error('Error parsing aiRequirementsWeighted:', e);
        setIfCurrent(() => setRequirements(null));
      }
    } else {
      setIfCurrent(() => setRequirements(null));
    }

    const loadIndustryNames = async () => {
      if (vacancy.industry) {
        let industryIds: string[] = [];
        if (Array.isArray(vacancy.industry)) {
          industryIds = vacancy.industry.filter(Boolean).map(String);
        } else if (typeof vacancy.industry === 'string' && vacancy.industry.trim()) {
          industryIds = vacancy.industry.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
        
        if (industryIds.length > 0) {
          try {
            const names = await Promise.all(
              industryIds.map(async (id) => {
                try {
                  const name = await getIndustryNameById(String(id).trim());
                  return name && name !== id ? name : id;
                } catch (error) {
                  console.error(`Error loading industry name for ID ${id}:`, error);
                  return String(id);
                }
              })
            );
            setIfCurrent(() => setIndustryName(names.filter(Boolean).join(', ')));
          } catch (error) {
            console.error('Error loading industry names:', error);
            setIfCurrent(() => setIndustryName(industryIds.join(', ')));
          }
        } else {
          setIfCurrent(() => setIndustryName(''));
        }
      } else {
        setIfCurrent(() => setIndustryName(''));
      }
    };

    const loadSpecializationNames = async () => {
      if (vacancy.specialization) {
        let specializationIds: string[] = [];
        if (Array.isArray(vacancy.specialization)) {
          specializationIds = vacancy.specialization.filter(Boolean).map(String);
        } else if (typeof vacancy.specialization === 'string' && vacancy.specialization.trim()) {
          specializationIds = vacancy.specialization.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
        
        if (specializationIds.length > 0) {
          try {
            const names = await Promise.all(
              specializationIds.map(async (id) => {
                try {
                  const normalizedId = String(id).trim();
                  const name = await getStoredSpecializationName(normalizedId);
                  return name && name !== normalizedId ? name : normalizedId;
                } catch (error) {
                  console.error(`Error loading specialization name for ID ${id}:`, error);
                  return String(id);
                }
              })
            );
            setIfCurrent(() => setSpecializationName(names.filter(Boolean).join(', ')));
          } catch (error) {
            console.error('Error loading specialization names:', error);
            setIfCurrent(() => setSpecializationName(specializationIds.join(', ')));
          }
        } else {
          setIfCurrent(() => setSpecializationName(''));
        }
      } else {
        setIfCurrent(() => setSpecializationName(''));
      }
    };

    void loadIndustryNames();
    void loadSpecializationNames();

    // Загрузка данных HH вакансий
    const loadHHVacancies = async () => {
      // Загрузка основной вакансии
      if (vacancy.hhVacancyId) {
        try {
          const response = await getHHVacancyById(vacancy.hhVacancyId);
          if (isCurrentDerivedRequest() && response?.vacancy) {
            setHhMainVacancy({
              id: vacancy.hhVacancyId,
              name: response.vacancy.name || `Вакансия ${vacancy.hhVacancyId}`
            });
          }
        } catch (err) {
          console.error('Error loading main HH vacancy:', err);
          setIfCurrent(() => setHhMainVacancy({
            id: vacancy.hhVacancyId,
            name: `Вакансия ${vacancy.hhVacancyId}`
          }));
        }
      } else {
        setIfCurrent(() => setHhMainVacancy(null));
      }

      // Загрузка дополнительных вакансий
      if (vacancy.hhAdditionalVacancyIds && vacancy.hhAdditionalVacancyIds.length > 0) {
        const additionalVacancies = await Promise.all(
          vacancy.hhAdditionalVacancyIds.map(async (vacancyId: string) => {
            try {
              const response = await getHHVacancyById(vacancyId);
              return {
                id: vacancyId,
                name: response?.vacancy?.name || `Вакансия ${vacancyId}`
              };
            } catch (err) {
              console.error(`Error loading HH vacancy ${vacancyId}:`, err);
              return {
                id: vacancyId,
                name: `Вакансия ${vacancyId}`
              };
            }
          })
        );
        setIfCurrent(() => setHhAdditionalVacancies(additionalVacancies));
      } else {
        setIfCurrent(() => setHhAdditionalVacancies([]));
      }
    };

    void loadHHVacancies();

    // Загрузка названий городов (конвертация ID в названия)
    if (vacancy.cities && vacancy.cities.length > 0) {
      const loadCityNames = async () => {
        const names = await Promise.all(
          vacancy.cities.map(async (cityId: string) => {
            // Если это уже название (не числовой ID) - оставляем как есть
            if (!/^\d+$/.test(cityId)) {
              return cityId;
            }
            try {
              const name = await getAreaNameById(cityId);
              return name || cityId;
            } catch {
              return cityId;
            }
          })
        );
        setIfCurrent(() => setCityNames(names));
      };
      loadCityNames();
    } else {
      setIfCurrent(() => setCityNames([]));
    }

    const vacancyCitizenship = Array.isArray((vacancy as any).citizenship)
      ? (vacancy as any).citizenship.map((id: string) => String(id))
      : typeof (vacancy as any).citizenship === 'string' && (vacancy as any).citizenship.trim()
      ? (vacancy as any).citizenship.split(',').map((value: string) => value.trim()).filter(Boolean)
      : [];

    if (vacancyCitizenship.length > 0) {
      void fetchHHCountries()
        .then(() => {
          setIfCurrent(() => setCitizenshipNames(vacancyCitizenship.map((id: string) => getCountryNameById(String(id)))));
        })
        .catch(() => {
          setIfCurrent(() => setCitizenshipNames(vacancyCitizenship.map((id: string) => String(id))));
        });
    } else {
      setIfCurrent(() => setCitizenshipNames([]));
    }

    return () => {
      isCancelled = true;
    };
  }, [vacancy, vacancyId, vacancyViewScope]);

  const loadVacancyData = useCallback(async () => {
    if (!vacancyId) return;

    const requestSeq = ++vacancyLoadSeqRef.current;
    const requestVacancyId = vacancyId;
    const requestScope = vacancyViewScope;
    const isCurrentRequest = () =>
      vacancyLoadSeqRef.current === requestSeq
      && currentVacancyIdRef.current === requestVacancyId
      && vacancyViewScopeRef.current === requestScope;

    setIsLoading(true);
    setLoadedVacancyIdentity(null);
    setVacancy(null);
    setCreatorData({});
    setHasQuestions(false);

    try {
      const data = await apiService.getPosition(requestVacancyId);
      if (!isCurrentRequest()) return;

      setVacancy(data);
      setLoadedVacancyIdentity({ id: requestVacancyId, scope: requestScope });
      setSelectedVacancy(data);
      setCreatorData({});

      if (data.createdBy) {
        try {
          const creator = await apiService.getAccountById(data.createdBy);
          if (!isCurrentRequest()) return;

          if (creator) {
            setCreatorData({
              firstName: creator.firstName,
              lastName: creator.lastName,
              email: creator.email,
            });
          }
        } catch (err) {
          if (!isCurrentRequest()) return;
          console.error('Error loading creator data:', err);
        }
      }

      try {
        const questionsData = await apiService.getQuestions(requestVacancyId);
        if (!isCurrentRequest()) return;

        setHasQuestions((questionsData?.questions?.length ?? 0) > 0);
      } catch {
        if (!isCurrentRequest()) return;
        setHasQuestions(false);
      }
    } catch (error) {
      if (!isCurrentRequest()) return;

      console.error('Error loading vacancy:', error);
      const status = getHttpStatus(error);
      const accessErrorMessage = getVacancyLoadAccessErrorMessage(status);

      if (accessErrorMessage) {
        setVacancy(null);
        setLoadedVacancyIdentity(null);
        setCreatorData({});
        setHasQuestions(false);
        useVacancyStore.getState().setSelectedVacancyById(null);
        toast.error(accessErrorMessage);
        navigate('/recruiter/vacancy', { replace: true });
        return;
      }

      setVacancy(null);
      setLoadedVacancyIdentity(null);
      setCreatorData({});
      setHasQuestions(false);
      toast.error('Не удалось загрузить данные вакансии');
    } finally {
      if (isCurrentRequest()) {
        setIsLoading(false);
      }
    }
  }, [navigate, setSelectedVacancy, vacancyId, vacancyViewScope]);

  useEffect(() => {
    if (vacancyId) {
      void loadVacancyData();
    }
  }, [loadVacancyData, vacancyId]);

  const handleStatusChange = async (newStatus: PositionStatusEnum) => {
    if (!vacancyId) return;

    try {
      await apiService.updatePosition(vacancyId, { status: newStatus });
      setVacancy({ ...vacancy, status: newStatus });
      setIsStatusDropdownOpen(false);
      toast.success('Статус вакансии обновлен');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Не удалось обновить статус');
    }
  };

  const handleEdit = () => {
    navigate(`/recruiter/vacancy/edit-data/${vacancyId}`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  const getStatusLabel = (status: PositionStatusEnum) => {
    const statusMap: Record<PositionStatusEnum, string> = {
      [PositionStatusEnum.ACTIVE]: 'Активная',
      [PositionStatusEnum.PAUSED]: 'Пауза',
      [PositionStatusEnum.ARCHIVED]: 'Архив',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: PositionStatusEnum) => {
    const colorMap: Record<PositionStatusEnum, string> = {
      [PositionStatusEnum.ACTIVE]: '#10b981',
      [PositionStatusEnum.PAUSED]: '#f59e0b',
      [PositionStatusEnum.ARCHIVED]: '#6b7280',
    };
    return colorMap[status] || '#6b7280';
  };

  const isCurrentVacancyLoaded = Boolean(
    vacancy
    && loadedVacancyIdentity?.id === vacancyId
    && loadedVacancyIdentity.scope === vacancyViewScope
  );

  if (isLoading || (vacancy && !isCurrentVacancyLoaded)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f6f1]">
        <div className="text-lg">Загрузка данных вакансии...</div>
      </div>
    );
  }

  if (!vacancy || !isCurrentVacancyLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f6f1]">
        <div className="text-lg">Вакансия не найдена</div>
      </div>
    );
  }

  return (
    <div className="bg-[#f5f6f1]">
      <div className="w-full max-w-full px-6">
        <div className="sticky top-0 z-30 flex items-center justify-between bg-[#f5f6f1] -mx-6 px-6 py-4 mb-6 border-b border-transparent transition-all duration-200">
          <h1 className="text-2xl ml-2 font-semibold text-[#0a0d14]">Вакансия</h1>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="font-medium whitespace-nowrap text-sm text-[#525866]">Создатель:</span>
              {creatorData.firstName || creatorData.lastName ? (
                <div className="flex items-center gap-1.5 px-2 py-1 text-sm text-[#525866]">
                  <div className="w-5 h-5 rounded-full bg-[#e0e7ff] flex items-center justify-center text-[10px] font-medium text-[#4f46e5] flex-shrink-0">
                    {creatorData.firstName?.[0] || ''}{creatorData.lastName?.[0] || ''}
                  </div>
                  <span className="text-sm">
                    {`${creatorData.firstName || ''} ${creatorData.lastName || ''}`.trim()}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-[#868c98]">Не указано</span>
              )}
            </div>
            {vacancy.createdAt && (
              <div className="flex items-center gap-2">
                <span className="font-medium whitespace-nowrap text-sm text-[#525866]">Создано:</span>
                <span className="text-sm text-[#525866]">{formatDate(vacancy.createdAt)}</span>
              </div>
            )}

            <VacancyStatusDropdown
              status={vacancy.status}
              isOpen={isStatusDropdownOpen}
              onToggle={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              onClose={() => setIsStatusDropdownOpen(false)}
              onStatusChange={handleStatusChange}
              getStatusLabel={getStatusLabel}
              getStatusColor={getStatusColor}
            />

            <button
              onClick={handleEdit}
              className="box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip h-11 px-3 relative rounded-2xl shrink-0 text-white font-medium transition-colors text-sm"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary;
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="2">
                <path d="M11.333 2A1.886 1.886 0 0 1 14 4.667l-9 9-3.667 1 1-3.667 9-9z" />
                <path d="M10.333 3L13 5.667" />
              </svg>
              Редактировать
            </button>
          </div>
        </div>

        <div className="flex gap-0 items-start">
          <div className="flex-1 min-w-0">
        <div className="bg-white rounded-xl p-8 space-y-8">
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold text-[#0a0d14]">
                {vacancy.title || 'Название вакансии'}
              </h2>
            </div>

            {/* HH.ru вакансии */}
            {(hhMainVacancy || hhAdditionalVacancies.length > 0) && (
              <div className="space-y-2">
                {hhMainVacancy && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[#525866]">
                      Вакансия <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">hh.ru</span>
                    </span>
                    <a
                      href={`https://hh.ru/vacancy/${hhMainVacancy.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
                    >
                      {hhMainVacancy.name}
                      <svg className="w-3.5 h-3.5 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
                {hhAdditionalVacancies.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[#525866]">
                      Дополнительные вакансии <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">hh.ru</span>
                    </span>
                    {hhAdditionalVacancies.map((hv) => (
                      <a
                        key={hv.id}
                        href={`https://hh.ru/vacancy/${hv.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
                      >
                        {hv.name}
                        <svg className="w-3.5 h-3.5 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 text-base text-[#000000]">
              <span>
                {cityNames.length > 0
                  ? cityNames.join(', ')
                  : vacancy.cities && vacancy.cities.length > 0
                  ? vacancy.cities.join(', ')
                  : <span className="text-[#868c98]">Не указано</span>}
              </span>
              <span>・</span>
              <span>
                {vacancy.experienceInPosition ? `Опыт в должности ${vacancy.experienceInPosition.split(',').map((s: string) => getExperienceInPositionLabel(s.trim())).join(', ')}` : <span>Опыт в должности <span className="text-[#868c98]">Не указано</span></span>}
              </span>
              <span>・</span>
              <span>
                {vacancy.salaryFrom && vacancy.salaryTo
                  ? `${formatSalaryDisplay(vacancy.salaryFrom)}-${formatSalaryDisplay(vacancy.salaryTo)} ${vacancy.salaryCurrency || 'RUR'}`
                  : vacancy.salaryFrom
                  ? `от ${formatSalaryDisplay(vacancy.salaryFrom)} ${vacancy.salaryCurrency || 'RUR'}`
                  : vacancy.salaryTo
                  ? `до ${formatSalaryDisplay(vacancy.salaryTo)} ${vacancy.salaryCurrency || 'RUR'}`
                  : <span className="text-[#868c98]">Не указано</span>}
              </span>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!vacancyId) return;
                    if (!hasQuestions) {
                      toast.error('Нет вопросов для AI-скрининга');
                      return;
                    }
                    const base = (process.env.REACT_APP_CANDIDATE_BASE_URL || window.location.origin).toString().replace(/\/+$/, '');
                    const url = `${base}/candidate/interview/${vacancyId}`;
                    handleCopyLink('ai-screening', url);
                  }}
                  className={[
                    'inline-flex items-center gap-2 px-2.5 h-10 rounded-[12px] text-sm font-medium transition-opacity',
                    vacancy.aiScreeningEnabled ? 'bg-[#effaf6] text-[#38c793] hover:opacity-80' : 'bg-[#fef3eb] text-[#f17b2c] hover:opacity-90'
                  ].join(' ')}
                >
                  <LinkCopyIconComponent className="w-5 h-5 flex-shrink-0" />
                  {copiedLinkKey === 'ai-screening'
                    ? 'Скопировано'
                    : vacancy.aiScreeningEnabled
                    ? 'AI-скрининг подключен'
                    : 'AI-скрининг не подключен'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const slug = (vacancy as any)?.publicSlug;
                    if (!slug) {
                      toast.error('Публичная ссылка недоступна');
                      return;
                    }
                    const base = window.location.origin.toString().replace(/\/+$/, '');
                    const userId = useAuthStore.getState().user?.id;
                    const recr = userId ? btoa(String(userId)) : null;
                    const url = recr ? `${base}/vacancy/${slug}?recr=${recr}` : `${base}/vacancy/${slug}`;
                    handleCopyLink('hr-interview', url);
                  }}
                  className="inline-flex items-center gap-2 px-3 h-10 rounded-[12px] text-sm font-medium bg-[#F6F8FA] text-[#0A0D14] border border-[#E6E8EC] hover:bg-[#EEF0F2] transition-colors"
                >
                  {copiedLinkKey === 'hr-interview' ? 'Скопировано' : 'Запись на HR-интервью'}
                  <LinkCopyCurrentIconComponent className="w-5 h-5 flex-shrink-0 text-[#0A0D14]" />
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-2 text-sm" data-testid="vacancy-info-block">
            <div data-testid="vacancy-info-row" data-field="city">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Город:</span>{' '}
              <span data-testid="vacancy-info-value" className={(cityNames.length > 0 || (vacancy.cities && vacancy.cities.length > 0)) ? "text-[#0a0d14]" : "text-[#868c98]"}>
                {cityNames.length > 0 
                  ? cityNames.join(', ') 
                  : vacancy.cities && vacancy.cities.length > 0 
                  ? vacancy.cities.join(', ') 
                  : 'Не указано'}
              </span>
            </div>
            <div data-testid="vacancy-info-row" data-field="experience">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Опыт работы:</span>{' '}
              <span data-testid="vacancy-info-value" className={([
                  vacancy.experienceInPosition && `В должности ${vacancy.experienceInPosition.split(',').map((s: string) => getExperienceInPositionLabel(s.trim())).join(', ')}`,
                  vacancy.experiencePeriod && `Период в отрасли: ${getExperiencePeriodLabel(vacancy.experiencePeriod)}`,
                  vacancy.totalExperience && `Общий ${vacancy.totalExperience.split(',').map((s: string) => getExperienceInPositionLabel(s.trim())).join(', ')}`,
                ].filter(Boolean).length > 0) ? "text-[#0a0d14]" : "text-[#868c98]"}>
                {[
                  vacancy.experienceInPosition && `В должности ${vacancy.experienceInPosition.split(',').map((s: string) => getExperienceInPositionLabel(s.trim())).join(', ')}`,
                  vacancy.experiencePeriod && `Период в отрасли: ${getExperiencePeriodLabel(vacancy.experiencePeriod)}`,
                  vacancy.totalExperience && `Общий ${vacancy.totalExperience.split(',').map((s: string) => getExperienceInPositionLabel(s.trim())).join(', ')}`,
                ]
                  .filter(Boolean)
                  .join(', ') || 'не указано'}
              </span>
            </div>
            <div data-testid="vacancy-info-row" data-field="salary">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Зарплатные ожидания:</span>{' '}
              <span data-testid="vacancy-info-value" className={(vacancy.salaryFrom || vacancy.salaryTo) ? "text-[#0a0d14]" : "text-[#868c98]"}>
                {vacancy.salaryFrom && vacancy.salaryTo
                  ? `${formatSalaryDisplay(vacancy.salaryFrom)}-${formatSalaryDisplay(vacancy.salaryTo)} ${vacancy.salaryCurrency || 'RUR'}`
                  : vacancy.salaryFrom
                  ? `от ${formatSalaryDisplay(vacancy.salaryFrom)} ${vacancy.salaryCurrency || 'RUR'}`
                  : vacancy.salaryTo
                  ? `до ${formatSalaryDisplay(vacancy.salaryTo)} ${vacancy.salaryCurrency || 'RUR'}`
                  : 'Не указано'}
              </span>
            </div>
            <div data-testid="vacancy-info-row" data-field="education">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Образование:</span>{' '}
              <span data-testid="vacancy-info-value" className={vacancy.education ? "text-[#0a0d14]" : "text-[#868c98]"}>
                {vacancy.education
                  ? String(vacancy.education).split(',').map((v: string) => {
                      const opt = EDUCATION_OPTIONS.find((o) => o.value === v.trim());
                      return opt ? opt.label : v.trim();
                    }).join(', ')
                  : 'Не указано'}
              </span>
            </div>
            <div data-testid="vacancy-info-row" data-field="industry">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Отрасль:</span>{' '}
              <span data-testid="vacancy-info-value" className={(industryName || vacancy.industry) ? "text-[#0a0d14]" : "text-[#868c98]"}>{industryName || vacancy.industry || 'Не указано'}</span>
            </div>
            <div data-testid="vacancy-info-row" data-field="specialization">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Специализация:</span>{' '}
              <span data-testid="vacancy-info-value" className={(specializationName || vacancy.specialization) ? "text-[#0a0d14]" : "text-[#868c98]"}>{specializationName || vacancy.specialization || 'Не указано'}</span>
            </div>
            <div data-testid="vacancy-info-row" data-field="search-status">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Статус поиска:</span>{' '}
              <span data-testid="vacancy-info-value" className="text-[#0a0d14]">Полная занятость</span>
            </div>
            <div data-testid="vacancy-info-row" data-field="work-format">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Формат работы:</span>{' '}
              <span data-testid="vacancy-info-value" className={vacancy.workFormat ? "text-[#0a0d14]" : "text-[#868c98]"}>{vacancy.workFormat || 'Не указано'}</span>
            </div>
            <div data-testid="vacancy-info-row" data-field="gender">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Пол:</span>{' '}
              <span data-testid="vacancy-info-value" className="text-[#0a0d14]">
                {vacancy.gender === 'any'
                  ? 'Любой'
                  : vacancy.gender === 'male'
                  ? 'М'
                  : vacancy.gender === 'female'
                  ? 'Ж'
                  : 'Любой'}
              </span>
            </div>
            <div data-testid="vacancy-info-row" data-field="citizenship">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Гражданство:</span>{' '}
              <span data-testid="vacancy-info-value" className={citizenshipNames.length > 0 ? "text-[#0a0d14]" : "text-[#868c98]"}>
                {citizenshipNames.length > 0 ? citizenshipNames.join(', ') : 'Не указано'}
              </span>
            </div>
            <div data-testid="vacancy-info-row" data-field="age">
              <span className="text-[#868c98]" data-testid="vacancy-info-label">Возраст:</span>{' '}
              <span data-testid="vacancy-info-value" className={(vacancy.ageFrom && vacancy.ageTo) ? "text-[#0a0d14]" : "text-[#868c98]"}>
                {vacancy.ageFrom && vacancy.ageTo ? `${vacancy.ageFrom}-${vacancy.ageTo} лет` : 'Не указано'}
              </span>
            </div>
          </div>

          <div className="pt-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#0a0d14] mb-3">Описание</h3>
              {vacancy.description ? (
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-[#000000]" dangerouslySetInnerHTML={{ __html: vacancy.description }} />
                  </div>
                </div>
              ) : (
                <div className="text-sm text-[#868c98]">Описание не добавлено</div>
              )}
            </div>

            {vacancy.companyDescription && (
              <div>
                <h3 className="text-lg font-semibold text-[#0a0d14] mt-2 mb-3">О компании и команде</h3>
                <div className="text-sm text-[#000000]" dangerouslySetInnerHTML={{ __html: vacancy.companyDescription }} />
              </div>
            )}

            {vacancy.socialPackageText && (
              <div>
                <h3 className="text-lg font-semibold text-[#0a0d14] mt-2 mb-3">Что входит в соц. пакет</h3>
                <div className="text-sm text-[#000000] whitespace-pre-line">{vacancy.socialPackageText}</div>
              </div>
            )}

            {vacancy.topics && vacancy.topics.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-[#0a0d14] mt-2 mb-3">Ключевые навыки</h3>
                <div className="flex flex-wrap gap-2">
                  {vacancy.topics.map((topic: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-[#fef3f2] text-[#dc2626] text-sm rounded-lg border border-[#fecaca]"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
          </div>{/* /flex-1 content column */}

          {/* Requirements panel (read-only, view mode) — always visible, even when empty */}
          {showReqPanel && (
            <div
              ref={reqPanelCallbackRef}
              className="shrink-0 sticky top-[72px] z-[35] ml-5 overflow-hidden ai-slide-in-right flex flex-col"
              style={{ maxHeight: 'calc(100vh - 100px)' }}
            >
              <RequirementsPanel
                required={requirements?.required || []}
                optional={requirements?.optional || []}
                readOnly
                onCollapse={() => setShowReqPanel(false)}
                onRegenerateWithAi={vacancyId ? async () => {
                  try {
                    const newJson = await apiService.regeneratePositionAiRequirements(vacancyId);
                    const parsed = JSON.parse(newJson);
                    const next = {
                      required: (parsed.required || []).map((r: any, i: number) => ({ id: `req-${i}`, text: r.text, weight: r.weight ?? 7 })),
                      optional: (parsed.optional || []).map((r: any, i: number) => ({ id: `opt-${i}`, text: r.text, weight: r.weight ?? 3 })),
                    };
                    setRequirements(next);
                    return next;
                  } catch (e) {
                    console.error('Failed to regenerate AI requirements:', e);
                    return null;
                  }
                } : undefined}
              />
            </div>
          )}

          {/* Collapsed bookmark */}
          {!showReqPanel && (
            <div
              ref={reqBookmarkCallbackRef}
              className="shrink-0 sticky top-0 flex flex-col items-center px-5 ml-5 rounded-[16px] cursor-pointer select-none ai-sidebar-in"
              style={{ backgroundColor: 'rgba(55, 93, 251, 0.15)' }}
              onClick={() => setShowReqPanel(true)}
            >
              <div className="mt-4 w-10 h-10 rounded-[10px] flex items-center justify-center bg-white shadow-sm shrink-0">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M10 3L2 7.5l8 4.5 8-4.5L10 3z" stroke="#375DFB" strokeWidth="1.4" strokeLinejoin="round"/><path d="M5 9.5v4.5c0 1.5 2.2 3 5 3s5-1.5 5-3V9.5" stroke="#375DFB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <span
                  className="text-[12px] font-semibold tracking-[0.1em] text-[#0a0d14] whitespace-nowrap"
                  style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                  ТРЕБОВАНИЯ
                </span>
              </div>
              <div className="mb-4 w-9 h-9 rounded-[10px] flex items-center justify-center bg-white shadow-sm shrink-0">
                <img src={aiPanelExpandIcon} alt="Expand" className="w-[10px] h-[10px]" />
              </div>
            </div>
          )}
        </div>{/* /flex row */}
      </div>
    </div>
  );
}
